<template>
	<v-container fluid>
		<v-card>
			<v-toolbar color="primary" density="comfortable">
				<v-toolbar-title class="text-white">Tickets de support</v-toolbar-title>
				<v-spacer />
				<div class="d-flex ga-2">
					<table-header-buttons :attr="toolTipText" :has-selected-lines="selectedTicketIds.length > 0"
						@add="add" @remove="remove" @reload="reloadData" @export="exportTable"
						@search="updateSearchPanel" />
				</div>
			</v-toolbar>

			<Table :loading="loading" :search-field="true" :columns="columns" :items="displayedTickets"
				:show-select="true" storageKey="supportTicketTablePaginationState" @edit="edit" @remove="removeSingle"
				@row-clicked="rowClicked" @selectionChanged="selectionChanged" />
		</v-card>

		<v-overlay :model-value="loading" class="align-center justify-center">
			<v-progress-circular indeterminate size="64" />
		</v-overlay>

		<SearchPanel :right-drawer="rightDrawer" @cancel-search="cancelSearch" @search-data="searchTickets">
			<v-row>
				<v-col cols="11" offset="1">
					<v-text-field label="Appelant" v-model="searchFilter.caller" />
				</v-col>
			</v-row>
			<v-row>
				<v-col cols="11" offset="1">
					<v-text-field label="Téléphone" v-model="searchFilter.telephone" />
				</v-col>
			</v-row>
			<v-row>
				<v-col cols="11" offset="1">
					<v-text-field label="Laverie" v-model="searchFilter.laverie" />
				</v-col>
			</v-row>
			<v-row>
				<v-col cols="11" offset="1">
					<v-text-field label="Adresse" v-model="searchFilter.address" />
				</v-col>
			</v-row>
		</SearchPanel>

		<ConfirmDialog v-model="dialog" :dialog-title="dialogTitle" :dialog-text="dialogText" @confirm="onConfirm"
			@cancel="onCancel" />
	</v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import SearchPanel from "@/components/SearchPanel.vue";
import Table from "@/components/Table.vue";
import TableHeaderButtons from "@/components/TableHeaderButtons.vue";
import { useAppStore } from "@/store/app";
import { getAllLaveries } from "@/utils/laverie-api";
import { deleteSupportTicket, fetchSupportTickets } from "@/utils/support-ticket-api";

import type { Entity, Laverie, SupportTicket, TableColumn } from "@/types";

interface SupportTicketTableItem extends Entity {
	date: Date;
	hour: string;
	callerType: string;
	caller: string;
	telephone: string;
	laverie: string;
	address: string;
	ticketStatus: string;
	concernedService: string;
	ticketType: string;
	assigneeFullName: string;
}

const router = useRouter();
const appStore = useAppStore();

const dialog = ref(false);
const dialogTitle = ref("Suppression");
const dialogText = ref("Voulez-vous supprimer les tickets de support sélectionnés ?");
const showSearchPanel = ref(false);
const loading = ref(false);
const tickets = ref<SupportTicket[]>([]);
const laveries = ref<Laverie[]>([]);
const selectedTicketIds = ref<string[]>([]);
const searchFilter = ref({
	caller: "",
	telephone: "",
	laverie: "",
	address: "",
});

const columns: TableColumn[] = [
	{ title: "Statut", key: "ticketStatus" },
	{ title: "Assigné à", key: "assigneeFullName" },
	{ title: "Date", key: "date", align: "start" },
	{ title: "Heure", key: "hour" },
	{ title: "Contact", key: "caller" },
	{ title: "Type Contact", key: "callerType" },
	{ title: "Téléphone", key: "telephone" },
	{ title: "Laverie", key: "laverie" },
	{ title: "Adresse", key: "address" },
	{ title: "Service concerné", key: "concernedService" },
	{ title: "Type", key: "ticketType" },
	{ title: "", key: "actions", sortable: false },
];

const toolTipText = "un appel";

const rightDrawer = computed({
	get: () => showSearchPanel.value,
	set: (value: boolean) => {
		showSearchPanel.value = value;
	},
});

const tableItems = computed<SupportTicketTableItem[]>(() => {
	return tickets.value.map((ticket) => {
		const laverie = laveries.value.find((item) => item.id === ticket.laverieId);
		const address = laverie?.fullAddress
			?? [laverie?.address?.numero, laverie?.address?.rue, laverie?.address?.codepostale, laverie?.address?.ville]
				.filter(Boolean)
				.join(" ");

		return {
			id: ticket.id,
			date: ticket.creationDate,
			hour: ticket.creationTime,
			callerType: ticket.callerType ?? "",
			caller: ticket.caller ?? "",
			telephone: ticket.telephone ?? "",
			laverie: laverie?.name ?? String(laverie?.numeroDeContrat ?? ""),
			address: address ?? "",
			ticketStatus: ticket.ticketStatus ?? "",
			concernedService: ticket.concernedService ?? "",
			ticketType: ticket.ticketType ?? "",
			assigneeFullName: ticket.assigneeFullName ?? "",
		};
	});
});

const displayedTickets = computed(() => {
	return tableItems.value.filter((ticket) => {
		const matchesCaller = !searchFilter.value.caller
			|| ticket.caller.toLowerCase().includes(searchFilter.value.caller.toLowerCase());
		const matchesTelephone = !searchFilter.value.telephone
			|| ticket.telephone.toLowerCase().includes(searchFilter.value.telephone.toLowerCase());
		const matchesLaverie = !searchFilter.value.laverie
			|| ticket.laverie.toLowerCase().includes(searchFilter.value.laverie.toLowerCase());
		const matchesAddress = !searchFilter.value.address
			|| ticket.address.toLowerCase().includes(searchFilter.value.address.toLowerCase());

		return matchesCaller && matchesTelephone && matchesLaverie && matchesAddress;
	});
});

const selectionChanged = (selected: string) => {
	selectedTicketIds.value = [...selected];
};

const rowClicked = (item: SupportTicketTableItem) => {
	router.push(`/support-tickets/${item.id}`);
};

const edit = (item: SupportTicketTableItem) => {
	router.push(`/support-tickets/${item.id}`);
};

const add = () => {
	router.push("/support-tickets/new");
};

const remove = () => {
	dialog.value = true;
};

const removeSingle = (item: SupportTicketTableItem) => {
	selectedTicketIds.value = item.id ? [item.id] : [];
	dialog.value = true;
};

const onConfirm = async () => {
	try {
		loading.value = true;
		dialog.value = false;
		await Promise.all(selectedTicketIds.value.map((id) => deleteSupportTicket(id)));
		await fetchData();
		selectedTicketIds.value = [];
		appStore.sendSuccessNotice("Support ticket has been deleted.");
	} catch (e) {
		// Ignore because it's handled in axios service
	} finally {
		loading.value = false;
	}
};

const onCancel = () => {
	dialog.value = false;
};

const searchTickets = () => {
	showSearchPanel.value = false;
};

const reloadData = () => {
	searchFilter.value = {
		caller: "",
		telephone: "",
		laverie: "",
		address: "",
	};
	fetchData();
};

const exportTable = () => {
	appStore.sendInfoNotice("Export non implémenté pour les appels.");
};

const updateSearchPanel = () => {
	rightDrawer.value = !rightDrawer.value;
};

const cancelSearch = () => {
	showSearchPanel.value = false;
};

const fetchData = async () => {
	try {
		loading.value = true;
		const [ticketData, laverieData] = await Promise.all([
			fetchSupportTickets(),
			getAllLaveries(),
		]);
		tickets.value = ticketData;
		laveries.value = laverieData;
	} finally {
		loading.value = false;
	}
};

onMounted(() => {
	fetchData();
});
</script>
